import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, ViewChild, ChangeDetectorRef } from '@angular/core'
import { Router, NavigationEnd } from '@angular/router'
import { DatetimeCustomEvent, IonicModule, IonModal, IonSelect, LoadingController, ModalController, ToastController, MenuController } from '@ionic/angular'
import { GeolocationService } from 'src/app/services/geolocation.service'
import { TranslateConfigService } from 'src/app/services/translate-config.service'
import { Subscription, filter } from 'rxjs'
import { GooglePlacesService, CitySuggestion } from 'src/app/services/google-places.service';
import { AppStorageService } from '../../services/app-storage.service';
import { StorageKeys } from '../../services/storage-keys';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectWebComponent } from '../select-web/select-web.component';

@Component({
  selector: 'app-filter-input',
  templateUrl: './filter-input.component.html',
  styleUrls: ['./filter-input.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule, SelectWebComponent]
})
export class FilterInputComponent implements OnInit, OnDestroy {
  @ViewChild('calendar', { static: false }) modal!: IonModal
  @ViewChild('selectInput') selectInput!: IonSelect

  @Output() map = new EventEmitter()
  @Output() filterChange = new EventEmitter<{id: string, value: any}>()

  @Input() color: string = 'light'
  @Input() interface: string = 'alert'
  @Input() isWeb: boolean = false
  @Input() input: any = {
    id: '',
    label: 'Filtro',
    placeholder: 'Seleccionar',
    type: 'select',
    options: []
  }
  @Input() value: any = null;

  selectedDate: string | null = null
  tempDate: any | null = null
  selectedValue: any = null;

  // Propiedades para manejo de ubicación
  locationDetails: {
    latitude?: number;
    longitude?: number;
    fullAddress?: string;
    city?: string;
    province?: string;
    postalCode?: string;
    country?: string;
  } = {};

  selectedRange: number = 0;
  private routerSubscription: Subscription = new Subscription();

  // Autocomplete (city) support
  autocompleteQuery: string = '';
  citySuggestions: CitySuggestion[] = [];
  showCitySuggestions: boolean = false;
  isLoadingSuggestions: boolean = false;
  private suggestionsTimer: any;
  selectedCityData: any = null; // {input: string, selected: boolean, city, country, placeId}

  constructor(
    private router: Router,
    private geolocationService: GeolocationService,
    private loadingController: LoadingController,
    private toastController: ToastController,
    private modalController: ModalController,
    private translateConfigService: TranslateConfigService,
    private cdr: ChangeDetectorRef,
  private menuCtrl: MenuController, // Agregar MenuController
  private googlePlaces: GooglePlacesService,
  private storage: AppStorageService,
  private translate: TranslateService
  ) {}

  ngOnInit() {
    // Inicializar el valor si viene desde el padre
    if (this.value !== null && this.value !== undefined) {
      this.selectedValue = this.value;

      // Para fechas, formatear correctamente
      if (this.input.type === 'date' && this.value) {
        const dateObj = new Date(this.value);
        this.selectedDate = dateObj.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        this.tempDate = this.value;
      }

      // Para ubicaciones, guardar detalles completos
      if (this.input.type === 'map' && typeof this.value === 'object') {
        if (this.value.address) {
          this.selectedValue = this.value.address;
        }
        this.updateLocationFromValue(this.value);
      }
    }

  // Suscribirse a los cambios de ruta para detectar regreso del mapa
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (event.url.includes('/listing') && this.input.type === 'map') {
          // Intentar recuperar datos de ubicación al regresar a listing
          setTimeout(() => {
            this.tryToRecoverLocationData();
          }, 100);
        }
      });

    // Intentar recuperar datos de ubicación al inicializar si es tipo map
    if (this.input.type === 'map') {
      this.tryToRecoverLocationData();
    }

    // Inicialización para autocomplete de ciudad
    if (this.input.type === 'autocomplete' && this.value) {
      // Si viene desde filtros guardados
      this.selectedCityData = this.value;
      this.autocompleteQuery = this.value.input || '';
      if (this.value.selected) {
        this.selectedValue = this.value.city || this.value.input;
      }
    }
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Actualiza los datos de ubicación desde un valor
   */
  private updateLocationFromValue(value: any) {
    if (value.lat && value.lng) {
      this.locationDetails.latitude = value.lat;
      this.locationDetails.longitude = value.lng;
    }
    if (value.fullAddress) {
      this.locationDetails.fullAddress = value.fullAddress;
    }
    if (value.address) {
      this.locationDetails.fullAddress = value.address;
    }
    if (value.city) {
      this.locationDetails.city = value.city;
    }
    if (value.province) {
      this.locationDetails.province = value.province;
    }
    if (value.postalCode) {
      this.locationDetails.postalCode = value.postalCode;
    }
    if (value.country) {
      this.locationDetails.country = value.country;
    }
    if (value.range !== undefined) {
      this.selectedRange = value.range;
    }
  }

  async openMap() {
    await this.handleMapAccess();
  }

  /**
   * Maneja el acceso al mapa verificando permisos de geolocalización
   */
  async handleMapAccess() {
    try {
      await this.openMapModal();
    } catch (error) {
      this.showPermissionErrorMessage();
    }
  }

  /**
   * Abre el mapa usando navegación
   */
  async openMapModal() {

    try {
      // IMPORTANTE: Cerrar el modal de filtros antes de navegar
      await this.menuCtrl.close('filters');


      // Esperar un poco para que se complete la animación de cierre
      await new Promise(resolve => setTimeout(resolve, 300));

      // Navegar al mapa pasando el modo filter y datos de contexto
      this.router.navigate(['/map'], {
        queryParams: {
          mode: 'filter',
          returnTo: '/listing'
        },
        state: {
          // Pasar la ubicación actual si existe
          currentLocation: this.locationDetails.latitude && this.locationDetails.longitude ? {
            lat: this.locationDetails.latitude,
            lng: this.locationDetails.longitude
          } : null,
          // Pasar la dirección actual si existe
          currentAddress: this.locationDetails.fullAddress || null,
          // Pasar el rango actual
          currentRange: this.selectedRange || 0,
          // Habilitar selector de rango para filtros
          showRangeSelector: true
        }
      });
    } catch (error) {
      // Si falla al cerrar el modal, al menos navegar
      this.router.navigate(['/map'], {
        queryParams: {
          mode: 'filter',
          returnTo: '/listing'
        },
        state: {
          currentLocation: this.locationDetails.latitude && this.locationDetails.longitude ? {
            lat: this.locationDetails.latitude,
            lng: this.locationDetails.longitude
          } : null,
          currentAddress: this.locationDetails.fullAddress || null,
          currentRange: this.selectedRange || 0,
          showRangeSelector: true
        }
      });
    }
  }

  /**
   * Intenta recuperar datos de ubicación de varias fuentes posibles
   */
  private tryToRecoverLocationData() {


    let dataProcessed = false;

    // Método 1: Desde el estado de navegación
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state && !dataProcessed) {
      const locationData = navigation.extras.state['locationData'];
      if (locationData) {

        this.updateLocationData(locationData);
        dataProcessed = true;
        this.storage.remove(StorageKeys.MAP_LOCATION_DATA);
        return;
      }
    }

    // Método 2: Desde el objeto history.state directamente
    const historyState = window.history.state;
    if (historyState && historyState.locationData && !dataProcessed) {

      this.updateLocationData(historyState.locationData);
      dataProcessed = true;
      this.storage.remove(StorageKeys.MAP_LOCATION_DATA);
      return;
    }

    // Método 3: Desde localStorage (respaldo)
    if (!dataProcessed) {
      const storedData = this.storage.get(StorageKeys.MAP_LOCATION_DATA);
      if (storedData) {
        try {
          const locationData = JSON.parse(storedData);

          this.updateLocationData(locationData);
          this.storage.remove(StorageKeys.MAP_LOCATION_DATA);
          dataProcessed = true;
        } catch (e) {
          this.storage.remove(StorageKeys.MAP_LOCATION_DATA);
        }
      }
    }
  }

  /**
   * Actualiza los datos de ubicación con la información recibida del mapa
   */
  private updateLocationData(locationData: any) {
    if (!locationData || !locationData.address) {
      return;
    }



    // Guardar datos de ubicación
    this.locationDetails = {
      latitude: locationData.lat,
      longitude: locationData.lng,
      fullAddress: locationData.address,
      city: locationData.city || '',
      province: locationData.province || '',
      postalCode: locationData.postalCode || '',
      country: locationData.country || ''
    };

    // Actualizar valor mostrado en el input
    this.selectedValue = locationData.address;

    // Actualizar el rango si viene en los datos
    if (locationData.range !== undefined) {
      this.selectedRange = locationData.range;
    }



    // Forzar detección de cambios
    this.cdr.detectChanges();

    // Emitir cambio al componente padre con todos los datos necesarios para el filtrado
    this.filterChange.emit({
      id: this.input.id,
      value: {
        address: locationData.address,
        lat: locationData.lat,
        lng: locationData.lng,
        range: this.selectedRange,
        ...this.locationDetails
      }
    });
  }

  async presentLoading(message: string = this.translate.instant('COMMON.LOADING')) {
    const loading = await this.loadingController.create({
      message: message,
      spinner: 'crescent'
    });
    await loading.present();
    return loading;
  }

  showCalendar() {
    if (this.modal) {
      this.modal.present()
    }
  }

  closeModal() {
    if (this.modal) {
      this.modal.dismiss()
    }
  }

  onDateChange(event: DatetimeCustomEvent) {
    this.tempDate = event.detail.value
    this.confirmDate()
  }

  confirmDate() {
    if (this.tempDate) {
      const dateObj = new Date(this.tempDate)
      this.selectedDate = dateObj.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })

      // Emitir el valor de la fecha seleccionada
      this.filterChange.emit({
        id: this.input.id,
        value: this.tempDate
      });
    }
    this.closeModal()
  }

  onSelectChange(event: any) {
  // Compatible con IonSelect (event.detail.value) y select web (event es el valor directamente)
  const value = event && event.detail ? event.detail.value : event;
  this.selectedValue = value;

  this.filterChange.emit({ id: this.input.id, value });
  }

  /**
   * Método público para resetear el componente
   */
  public reset() {
    this.selectedValue = null;
    this.selectedDate = null;
    this.tempDate = null;
    this.locationDetails = {};
    this.selectedRange = 0;
  // Reset autocomplete
  this.autocompleteQuery = '';
  this.citySuggestions = [];
  this.showCitySuggestions = false;
  this.selectedCityData = null;
    this.cdr.detectChanges();
  }

  // Métodos para mostrar mensajes de error
  private showPermissionErrorMessage() {
    this.toastController.create({
      message: this.translate.instant('FILTERS.LOCATION_ACCESS_ERROR'),
      duration: 3000,
      color: 'danger',
      position: 'bottom'
    }).then(toast => toast.present());
  }

  // --- AUTOCOMPLETE (CITY) LOGIC ---
  onCityInputChange(ev: any) {
    const value = (ev.target?.value || '').trim();
    this.autocompleteQuery = value;
    this.selectedValue = null; // limpiar visual seleccionado
    this.selectedCityData = { input: value, selected: false }; // emitir como no seleccionado

    // Emitir cambio provisional
    this.filterChange.emit({ id: this.input.id, value: this.selectedCityData });

    if (this.suggestionsTimer) {
      clearTimeout(this.suggestionsTimer);
    }
    if (value.length < 2) {
      this.citySuggestions = [];
      this.showCitySuggestions = false;
      return;
    }
    this.suggestionsTimer = setTimeout(() => {
      this.isLoadingSuggestions = true;
      this.googlePlaces.getCitySuggestions(value).subscribe({
        next: (suggestions) => {
          this.citySuggestions = suggestions;
          this.showCitySuggestions = suggestions.length > 0;
          this.isLoadingSuggestions = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.citySuggestions = [];
          this.showCitySuggestions = false;
          this.isLoadingSuggestions = false;
        }
      });
    }, 350); // debounce
  }

  selectCitySuggestion(s: CitySuggestion) {
    this.autocompleteQuery = s.mainText;
    this.selectedValue = s.mainText;
    this.showCitySuggestions = false;
    this.selectedCityData = {
      input: s.mainText,
      selected: true,
      city: s.mainText,
      country: s.secondaryText || '',
      placeId: s.placeId,
      description: s.description
    };
    this.filterChange.emit({ id: this.input.id, value: this.selectedCityData });
  }

  hideSuggestions() {
    setTimeout(() => {
      this.showCitySuggestions = false;
      this.cdr.detectChanges();
    }, 200); // small delay to allow click
  }
}
